import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import MetricsCard from './MetricsCard';

const BlogManagementPanel = () => {
  const [posts, setPosts] = useState([
    {
      id: 1,
      title: 'Top 10 Wedding Photography Trends for 2025',
      slug: 'wedding-photography-trends-2025',
      category: 'Photography',
      author: 'Admin',
      status: 'published',
      views: 1245,
      publishedAt: '2025-07-10T10:00:00Z',
      excerpt: 'Discover the latest trends in wedding photography that will dominate 2025...'
    },
    {
      id: 2,
      title: 'How to Choose the Perfect Wedding Venue',
      slug: 'choose-perfect-wedding-venue',
      category: 'Planning',
      author: 'Admin',
      status: 'draft',
      views: 0,
      publishedAt: null,
      excerpt: 'A comprehensive guide to selecting the ideal venue for your special day...'
    },
    {
      id: 3,
      title: 'Budget-Friendly Wedding Decoration Ideas',
      slug: 'budget-wedding-decoration-ideas',
      category: 'Decor',
      author: 'Admin',
      status: 'scheduled',
      views: 0,
      publishedAt: '2025-07-15T12:00:00Z',
      excerpt: 'Beautiful decoration ideas that won\'t break your wedding budget...'
    }
  ]);

  const [categories, setCategories] = useState([
    { id: 1, name: 'Photography', slug: 'photography', postCount: 12, color: 'blue' },
    { id: 2, name: 'Planning', slug: 'planning', postCount: 8, color: 'green' },
    { id: 3, name: 'Decor', slug: 'decor', postCount: 15, color: 'purple' },
    { id: 4, name: 'Fashion', slug: 'fashion', postCount: 6, color: 'pink' },
    { id: 5, name: 'Catering', slug: 'catering', postCount: 4, color: 'orange' }
  ]);

  const [newCategory, setNewCategory] = useState({ name: '', slug: '', color: 'blue' });
  const [activeTab, setActiveTab] = useState('posts');

  const blogStats = {
    totalPosts: posts.length,
    published: posts.filter(p => p.status === 'published').length,
    drafts: posts.filter(p => p.status === 'draft').length,
    totalViews: posts.reduce((sum, p) => sum + p.views, 0)
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return 'text-green-600 bg-green-100';
      case 'draft': return 'text-gray-600 bg-gray-100';
      case 'scheduled': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getCategoryColor = (color) => {
    const colors = {
      blue: 'bg-blue-100 text-blue-800',
      green: 'bg-green-100 text-green-800',
      purple: 'bg-purple-100 text-purple-800',
      pink: 'bg-pink-100 text-pink-800',
      orange: 'bg-orange-100 text-orange-800'
    };
    return colors[color] || colors.blue;
  };

  const handleAddCategory = () => {
    if (newCategory.name && newCategory.slug) {
      setCategories(prev => [...prev, {
        id: Date.now(),
        ...newCategory,
        postCount: 0
      }]);
      setNewCategory({ name: '', slug: '', color: 'blue' });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not published';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-xl p-6 text-white">
        <h2 className="font-heading font-bold text-2xl mb-2">
          Blog Management & Categories
        </h2>
        <p className="text-green-100">
          Manage blog content and organize categories
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <MetricsCard
          title="Total Posts"
          value={blogStats.totalPosts}
          icon="FileText"
          color="blue"
        />
        <MetricsCard
          title="Published"
          value={blogStats.published}
          icon="CheckCircle"
          color="green"
        />
        <MetricsCard
          title="Drafts"
          value={blogStats.drafts}
          icon="Edit"
          color="orange"
        />
        <MetricsCard
          title="Total Views"
          value={blogStats.totalViews.toLocaleString()}
          icon="Eye"
          color="purple"
        />
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-soft">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('posts')}
            className={`px-6 py-4 font-medium ${
              activeTab === 'posts' ?'text-primary border-b-2 border-primary' :'text-gray-500 hover:text-gray-700'
            }`}
          >
            Blog Posts
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            className={`px-6 py-4 font-medium ${
              activeTab === 'categories' ?'text-primary border-b-2 border-primary' :'text-gray-500 hover:text-gray-700'
            }`}
          >
            Categories
          </button>
        </div>

        <div className="p-6">
          {activeTab === 'posts' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading font-semibold text-xl text-text-primary">
                  Blog Posts
                </h3>
                <Button variant="primary">
                  <Icon name="Plus" size={16} className="mr-2" />
                  New Post
                </Button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Title
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Category
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Views
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Published
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {posts.map((post) => (
                      <tr key={post.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div>
                            <div className="font-medium text-gray-900">{post.title}</div>
                            <div className="text-sm text-gray-500">{post.excerpt}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                            {post.category}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(post.status)}`}>
                            {post.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {post.views.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(post.publishedAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          <Button variant="ghost" size="sm">
                            <Icon name="Eye" size={16} />
                          </Button>
                          <Button variant="ghost" size="sm">
                            <Icon name="Edit" size={16} />
                          </Button>
                          <Button variant="ghost" size="sm">
                            <Icon name="Trash2" size={16} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'categories' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading font-semibold text-xl text-text-primary">
                  Blog Categories
                </h3>
              </div>

              {/* Add New Category */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h4 className="font-medium text-gray-900 mb-4">Add New Category</h4>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <input
                    type="text"
                    placeholder="Category Name"
                    value={newCategory.name}
                    onChange={(e) => setNewCategory(prev => ({ ...prev, name: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  <input
                    type="text"
                    placeholder="Slug (URL friendly)"
                    value={newCategory.slug}
                    onChange={(e) => setNewCategory(prev => ({ ...prev, slug: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  <select
                    value={newCategory.color}
                    onChange={(e) => setNewCategory(prev => ({ ...prev, color: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="blue">Blue</option>
                    <option value="green">Green</option>
                    <option value="purple">Purple</option>
                    <option value="pink">Pink</option>
                    <option value="orange">Orange</option>
                  </select>
                  <Button variant="primary" onClick={handleAddCategory}>
                    <Icon name="Plus" size={16} className="mr-2" />
                    Add Category
                  </Button>
                </div>
              </div>

              {/* Categories Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categories.map((category) => (
                  <div key={category.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className={`px-3 py-1 text-sm font-medium rounded-full ${getCategoryColor(category.color)}`}>
                        {category.name}
                      </span>
                      <div className="flex space-x-1">
                        <Button variant="ghost" size="sm">
                          <Icon name="Edit" size={14} />
                        </Button>
                        <Button variant="ghost" size="sm">
                          <Icon name="Trash2" size={14} />
                        </Button>
                      </div>
                    </div>
                    <div className="text-sm text-gray-600">
                      <p>Slug: {category.slug}</p>
                      <p>Posts: {category.postCount}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogManagementPanel;